// src/pages/settings/EngieImportModal.tsx — import d'un fichier Excel Engie (ÉLEC et/ou GAZ),
// potentiellement plusieurs compteurs en une seule action — cf. CLAUDE.md, section "Connecteur
// engie". Deux modes selon `canEditEngieSource` (= canManageRoles, admin) :
//   - admin   : UI de mapping complète (choisir un PDL/PCE existant, en créer un nouveau avec un
//               id éditable, ou ignorer un compteur détecté).
//   - manager : aucune UI de mapping — l'id est calculé automatiquement (suggestEngieMeterId),
//               sans interaction. Un compteur dont l'id suggéré collisionne avec un PDL/PCE
//               existant d'une autre source est exclu (signalé, jamais écrasé silencieusement) —
//               seul un admin peut résoudre ce cas via le mode complet.
// "engie" n'est jamais assignable depuis PdlForm/PceForm — c'est ce modal qui est l'unique porte
// d'entrée de cette source.

import { useRef, useState } from "react";
import Modal from "./Modal";
import { SiteSelect } from "./SiteSelect";
import ConfirmModal, { type ConfirmModalProps } from "../../components/ConfirmModal";
import LoadingScreen from "../../components/LoadingScreen";
import { useMinDuration } from "../../hooks/useMinDuration";
import type { ClientRecord, SiteRecord } from "../../api/settingsApi";
import { upsertPdl, upsertPce, type PdlRecord, type PceRecord } from "../../api/metersApi";
import {
  validateEngieFile, ingestEngieFile,
  type EngieParseResult, type EngieMeterCandidate, type EngieMappingEntry, type EngieEnergyType,
} from "../../api/engieApi";

function slugify(s: string): string {
  return s.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "x";
}

/** Suggestion déterministe (site+énergie+libellé) — les compteurs Engie n'ont aucun identifiant
 * utilitaire réel (pas de PRM Enedis, pas de n° PCE GRDF). Utilisée telle quelle en mode manager,
 * comme simple pré-remplissage éditable en mode admin. */
export function suggestEngieMeterId(siteId: string, energyType: EngieEnergyType, label: string): string {
  return `engie-${slugify(siteId.replace(/@/g, "-"))}-${energyType}-${slugify(label)}`;
}

interface RowState { choice: string; newId: string; } // choice: "" (ignorer) | "__new__" | id d'un compteur existant

export function EngieImportModal({ sites, clients, pdls, pces, canEditEngieSource, showToast, onImported, onClose }: {
  sites: SiteRecord[]; clients: ClientRecord[]; pdls: PdlRecord[]; pces: PceRecord[];
  canEditEngieSource: boolean;
  showToast: (msg: string) => void;
  onImported: () => void;
  onClose: () => void;
}) {
  const [siteId,      setSiteId]      = useState("");
  const [includeElec, setIncludeElec] = useState(true);
  const [includeGaz,  setIncludeGaz]  = useState(false);
  const [drag,        setDrag]        = useState(false);
  const [filename,    setFilename]    = useState<string | null>(null);
  const [dataBase64,  setDataBase64]  = useState<string | null>(null);
  const [validating,  setValidating]  = useState(false);
  const [report,      setReport]      = useState<EngieParseResult | null>(null);
  const [showAllWarnings, setShowAllWarnings] = useState(false);
  const [validateErr, setValidateErr] = useState<string | null>(null);
  const [rows,        setRows]        = useState<Record<string, RowState>>({});
  const [ingesting,   setIngesting]   = useState(false);
  const ingestingDelayed = useMinDuration(ingesting);
  const [ingestErr,   setIngestErr]   = useState<string | null>(null);
  const [confirmModal, setConfirmModal] = useState<ConfirmModalProps | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const ref = useRef<HTMLInputElement>(null);

  const pdlsForSite = pdls.filter(p => p.siteId === siteId);
  const pcesForSite = pces.filter(p => p.siteId === siteId);

  const reset = () => { setFilename(null); setDataBase64(null); setReport(null); setValidateErr(null); setIngestErr(null); setRows({}); setShowAllWarnings(false); setPreviewOpen(false); };

  const handleFile = (file: File) => {
    reset();
    if (!file.name.toLowerCase().endsWith(".xlsx")) { setValidateErr("Format non supporté. Utilisez un fichier .xlsx."); return; }
    setFilename(file.name);
    const r = new FileReader();
    r.onload = async () => {
      const dataUrl = r.result as string;
      const b64 = dataUrl.substring(dataUrl.indexOf(",") + 1);
      setDataBase64(b64);
      setValidating(true);
      try {
        const rep = await validateEngieFile({ filename: file.name, dataBase64: b64 });
        setReport(rep);
        const initRows: Record<string, RowState> = {};
        for (const m of rep.meters) {
          initRows[m.key] = { choice: "", newId: suggestEngieMeterId(siteId, m.energyType, m.label) };
        }
        setRows(initRows);
      } catch (e) {
        setValidateErr((e as Error).message);
      } finally {
        setValidating(false);
      }
    };
    r.readAsDataURL(file);
  };

  const isIncluded = (m: EngieMeterCandidate) => m.energyType === "elec" ? includeElec : includeGaz;

  const createMeter = async (meterId: string, energyType: EngieEnergyType, label: string) => {
    if (energyType === "elec") {
      await upsertPdl({ id: meterId, siteId, source: "engie", label, segment: "", puissanceSouscrite: "", codePostal: "", sge: false });
    } else {
      await upsertPce({ id: meterId, siteId, source: "engie", label, codePostal: "" });
    }
  };

  const retagMeter = async (meterId: string, energyType: EngieEnergyType) => {
    if (energyType === "elec") {
      const p = pdls.find(x => x.id === meterId);
      if (p) await upsertPdl({
        id: p.id, segment: p.segment, puissanceSouscrite: p.puissanceSouscrite,
        codePostal: p.codePostal, siteId: p.siteId, sge: p.sge, source: "engie", label: p.label,
      });
    } else {
      const p = pces.find(x => x.id === meterId);
      if (p) await upsertPce({ id: p.id, codePostal: p.codePostal, siteId: p.siteId, source: "engie", label: p.label });
    }
  };

  const finishIngest = async (mapping: EngieMappingEntry[]) => {
    setIngesting(true); setIngestErr(null);
    try {
      const res = await ingestEngieFile({ siteId, filename: filename!, dataBase64: dataBase64!, mapping });
      showToast(`Import Engie intégré ✓ (${res.results.length} compteur${res.results.length > 1 ? "s" : ""})`);
      onImported();
      onClose();
    } catch (e) {
      setIngestErr((e as Error).message);
    } finally {
      setIngesting(false);
    }
  };

  // ── Mode admin : mapping complet, choix explicite par compteur ─────────────────
  const handleConfirmAdmin = async () => {
    if (!report) return;
    const mapping: EngieMappingEntry[] = [];
    const toCreate: Array<{ meterId: string; energyType: EngieEnergyType; label: string }> = [];
    const toRetag: Array<{ meterId: string; energyType: EngieEnergyType }> = [];
    const conflicts: Array<{ meterId: string; currentSource: string }> = [];

    for (const m of report.meters) {
      if (!isIncluded(m)) continue;
      const row = rows[m.key];
      if (!row || row.choice === "") continue;
      if (row.choice === "__new__") {
        const meterId = row.newId.trim();
        if (!meterId) continue;
        toCreate.push({ meterId, energyType: m.energyType, label: m.label });
        mapping.push({ key: m.key, meterId, energyType: m.energyType });
      } else {
        const meterId = row.choice;
        const existing = (m.energyType === "elec" ? pdls : pces).find(p => p.id === meterId);
        if (existing && existing.source !== "engie") conflicts.push({ meterId, currentSource: existing.source || "— Aucune —" });
        if (!existing || existing.source !== "engie") toRetag.push({ meterId, energyType: m.energyType });
        mapping.push({ key: m.key, meterId, energyType: m.energyType });
      }
    }

    if (mapping.length === 0) { setIngestErr("Aucun compteur mappé."); return; }

    const proceed = async () => {
      setIngesting(true); setIngestErr(null);
      try {
        for (const c of toCreate) await createMeter(c.meterId, c.energyType, c.label);
        for (const r of toRetag) await retagMeter(r.meterId, r.energyType);
        setIngesting(false);
        await finishIngest(mapping);
      } catch (e) {
        setIngestErr((e as Error).message);
        setIngesting(false);
      }
    };

    if (conflicts.length) {
      setConfirmModal({
        title: "Changer la source de compteurs existants ?",
        message: `${conflicts.length} compteur(s) déjà existant(s) sont dans une autre source (${conflicts.map(c => `${c.meterId} : ${c.currentSource}`).join(", ")}). L'import Engie va la remplacer par "engie". Continuer ?`,
        confirmLabel: "Continuer", danger: true,
        onCancel: () => setConfirmModal(null),
        onConfirm: () => { setConfirmModal(null); proceed(); },
      });
    } else {
      await proceed();
    }
  };

  // ── Mode manager : aucune interaction, id suggéré, conflits exclus ─────────────
  const handleConfirmManager = async () => {
    if (!report) return;
    const mapping: EngieMappingEntry[] = [];
    const toCreate: Array<{ meterId: string; energyType: EngieEnergyType; label: string }> = [];
    const excluded: string[] = [];

    for (const m of report.meters) {
      if (!isIncluded(m)) continue;
      const meterId = suggestEngieMeterId(siteId, m.energyType, m.label);
      const existing = (m.energyType === "elec" ? pdls : pces).find(p => p.id === meterId);
      if (existing && existing.source !== "engie") {
        excluded.push(`${m.label} (${meterId}, source actuelle : ${existing.source || "— Aucune —"})`);
        continue;
      }
      if (!existing) toCreate.push({ meterId, energyType: m.energyType, label: m.label });
      mapping.push({ key: m.key, meterId, energyType: m.energyType });
    }

    if (mapping.length === 0) {
      setIngestErr(excluded.length ? `Tous les compteurs sont en collision — contactez un administrateur : ${excluded.join(", ")}` : "Aucun compteur à importer (cases ÉLEC/GAZ décochées ?).");
      return;
    }

    setIngesting(true); setIngestErr(null);
    try {
      for (const c of toCreate) await createMeter(c.meterId, c.energyType, c.label);
      setIngesting(false);
      await finishIngest(mapping);
      if (excluded.length) showToast(`⚠ ${excluded.length} compteur(s) exclu(s) — collision de source, contactez un administrateur.`);
    } catch (e) {
      setIngestErr((e as Error).message);
      setIngesting(false);
    }
  };

  const canSubmit = !!report && !!siteId && (includeElec || includeGaz);

  return (
    <Modal title="📥 Importer un fichier Engie" onClose={onClose} wide fixedHeight>
      <div style={{display:"flex",flexDirection:"column",flex:1,minHeight:0}}>
        <div style={{overflowY:"auto",padding:"20px 24px",flex:1}}>
          <div className="mb-16">
            <label className="label">Site *</label>
            <SiteSelect value={siteId} onChange={id=>{setSiteId(id); reset();}} sites={sites} clients={clients}
              placeholder="— Choisir un site —" includeOrphans={false}/>
          </div>

          <div className="mb-16" style={{display:"flex",gap:16}}>
            <label style={{display:"flex",alignItems:"center",gap:6,fontSize:13,cursor:"pointer"}}>
              <input type="checkbox" checked={includeElec} onChange={e=>setIncludeElec(e.target.checked)}/>
              ⚡ Importer ÉLEC
            </label>
            <label style={{display:"flex",alignItems:"center",gap:6,fontSize:13,cursor:"pointer"}}>
              <input type="checkbox" checked={includeGaz} onChange={e=>setIncludeGaz(e.target.checked)}/>
              🔥 Importer GAZ
            </label>
          </div>

          {!siteId && <div style={{fontSize:12,color:"var(--text-faint)",fontStyle:"italic"}}>Choisissez un site pour activer l'import.</div>}

          {siteId && !filename && (
            <div onClick={()=>ref.current?.click()}
              onDragOver={e=>{e.preventDefault();setDrag(true);}}
              onDragLeave={()=>setDrag(false)}
              onDrop={e=>{e.preventDefault();setDrag(false);const f=e.dataTransfer.files[0];if(f)handleFile(f);}}
              style={{border:`2px dashed ${drag?"var(--green-dim)":"#ddd"}`,borderRadius:8,cursor:"pointer",
                padding:"18px 12px",display:"flex",alignItems:"center",justifyContent:"center",
                background:drag?"rgba(0,255,0,0.04)":"#fff",transition:"all 0.15s"}}>
              <span style={{fontSize:12,color:"#aaa",textAlign:"center"}}>
                Fichier Excel Engie (.xlsx)<br/>Glisser ici ou cliquer
              </span>
              <input ref={ref} type="file" accept=".xlsx" style={{display:"none"}}
                onChange={e=>{const f=e.target.files?.[0];if(f)handleFile(f);}}/>
            </div>
          )}

          {filename && (
            <div>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
                <span style={{fontSize:12,color:"var(--text-muted)"}}>📄 {filename}</span>
                <button onClick={reset} className="btn btn-ghost btn-sm" style={{fontSize:11,padding:"2px 8px"}}>✕ Changer de fichier</button>
              </div>

              {validating && <div style={{fontSize:12,color:"var(--text-faint)"}}>Vérification du fichier…</div>}
              {validateErr && (
                <div style={{background:"#fdecea",border:"1px solid #f5c6cb",borderRadius:6,padding:"10px 14px",fontSize:12,color:"var(--red)"}}>⚠ {validateErr}</div>
              )}

              {report && (
                <div style={{background:"#fff",border:"1px solid #e0e0e0",borderRadius:6,padding:"10px 14px",fontSize:12}}>
                  <div style={{marginBottom:8,display:"flex",alignItems:"center",justifyContent:"space-between",gap:8}}>
                    <span>Années détectées : <strong>{report.years.join(", ")}</strong></span>
                    <button onClick={()=>setPreviewOpen(true)} className="btn btn-ghost btn-sm" style={{fontSize:11,padding:"2px 8px"}}>👁 Visualiser les valeurs</button>
                  </div>

                  {report.ignoredSheets.length > 0 && (
                    <div style={{fontSize:11,color:"var(--text-faint)",marginBottom:8}}>
                      Onglets ignorés : {report.ignoredSheets.slice(0,8).join(", ")}{report.ignoredSheets.length>8?` … (+${report.ignoredSheets.length-8})`:""}
                    </div>
                  )}
                  {report.warnings.length > 0 && (
                    <div style={{fontSize:11,color:"#c8740a",marginBottom:8}}>
                      {(showAllWarnings ? report.warnings : report.warnings.slice(0,6)).map((w,i)=><div key={i}>⚠ {w}</div>)}
                      {report.warnings.length > 6 && (
                        <button onClick={()=>setShowAllWarnings(v=>!v)} className="btn btn-ghost btn-sm"
                          style={{fontSize:11,padding:"2px 6px",marginTop:4,color:"#c8740a"}}>
                          {showAllWarnings ? "Réduire" : `… (+${report.warnings.length-6} autres — afficher tout)`}
                        </button>
                      )}
                    </div>
                  )}

                  <div style={{display:"flex",flexDirection:"column",gap:8}}>
                    {report.meters.map(m => {
                      const included = isIncluded(m);
                      const row = rows[m.key];
                      const candidates = m.energyType === "elec" ? pdlsForSite : pcesForSite;
                      return (
                        <div key={m.key} style={{opacity:included?1:0.45,borderTop:"1px solid #f0f0f0",paddingTop:8}}>
                          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:canEditEngieSource&&included?6:0}}>
                            <span>{m.energyType==="elec"?"⚡":"🔥"}</span>
                            <strong>{m.label}</strong>
                            <span style={{color:"var(--text-faint)"}}>({m.unit || "kWh"})</span>
                            {!included && <span style={{color:"var(--text-faint)",fontStyle:"italic"}}>— détecté, non importé (case décochée)</span>}
                          </div>

                          {included && canEditEngieSource && (
                            <div style={{display:"flex",flexDirection:"column",gap:6}}>
                              <select value={row?.choice ?? ""} onChange={e=>setRows(prev=>({...prev,[m.key]:{...(prev[m.key]??{choice:"",newId:suggestEngieMeterId(siteId,m.energyType,m.label)}),choice:e.target.value}}))} className="field" style={{fontSize:12}}>
                                <option value="">— Ignorer —</option>
                                {candidates.map(c=><option key={c.id} value={c.id}>{c.id}{c.label?` — ${c.label}`:""}</option>)}
                                <option value="__new__">+ Créer un nouveau {m.energyType==="elec"?"PDL":"PCE"}…</option>
                              </select>
                              {row?.choice === "__new__" && (
                                <input value={row.newId} onChange={e=>setRows(prev=>({...prev,[m.key]:{...prev[m.key],newId:e.target.value}}))}
                                  className="field" style={{fontSize:12}} placeholder="Identifiant du nouveau compteur"/>
                              )}
                            </div>
                          )}

                          {included && !canEditEngieSource && (
                            <div style={{fontSize:11,color:"var(--text-muted)"}}>
                              → <code>{suggestEngieMeterId(siteId, m.energyType, m.label)}</code>
                              {(() => {
                                const suggested = suggestEngieMeterId(siteId, m.energyType, m.label);
                                const existing = (m.energyType==="elec"?pdlsForSite:pcesForSite).find(p=>p.id===suggested);
                                if (!existing) return " (nouveau)";
                                if (existing.source === "engie") return " (mise à jour)";
                                return <span style={{color:"var(--red)"}}> — ⚠ collision avec un compteur existant en source "{existing.source || "— Aucune —"}", sera exclu</span>;
                              })()}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {ingestErr && (
                <div style={{background:"#fdecea",border:"1px solid #f5c6cb",borderRadius:6,padding:"10px 14px",fontSize:12,color:"var(--red)",marginTop:8}}>⚠ {ingestErr}</div>
              )}
            </div>
          )}
        </div>

        <div style={{padding:"16px 24px",borderTop:"1px solid #f0f0f0",background:"#fff",flexShrink:0,display:"flex",justifyContent:"flex-end",gap:10}}>
          <button onClick={onClose} className="btn btn-ghost">Annuler</button>
          <button
            onClick={canEditEngieSource ? handleConfirmAdmin : handleConfirmManager}
            disabled={!canSubmit || ingesting}
            className="btn btn-black btn-lg">
            Confirmer l'import
          </button>
        </div>
      </div>

      {confirmModal && <ConfirmModal {...confirmModal}/>}
      {previewOpen && report && <EngieValuesPreviewModal meters={report.meters} isIncluded={isIncluded} onClose={()=>setPreviewOpen(false)}/>}
      {ingestingDelayed && <LoadingScreen message="Import Engie en cours…" overlay/>}
    </Modal>
  );
}

const MONTHS_SHORT = ["Jan","Fév","Mar","Avr","Mai","Jun","Jul","Aoû","Sep","Oct","Nov","Déc"];

function MonthlyValuesTable({ byYear }: { byYear: Record<string, (number | null)[]> }) {
  const years = Object.keys(byYear).sort();
  if (years.length === 0) return <div style={{fontSize:11,color:"var(--text-faint)",fontStyle:"italic"}}>Aucune valeur.</div>;
  return (
    <div style={{overflowX:"auto"}}>
      <table style={{borderCollapse:"collapse",fontSize:11,width:"100%"}}>
        <thead>
          <tr>
            <th style={{textAlign:"left",padding:"4px 8px",borderBottom:"1px solid #e0e0e0",color:"var(--text-faint)"}}>Année</th>
            {MONTHS_SHORT.map(m => <th key={m} style={{textAlign:"right",padding:"4px 8px",borderBottom:"1px solid #e0e0e0",color:"var(--text-faint)"}}>{m}</th>)}
          </tr>
        </thead>
        <tbody>
          {years.map(y => (
            <tr key={y}>
              <td style={{padding:"4px 8px",borderBottom:"1px solid #f5f5f5",fontWeight:600}}>{y}</td>
              {Array.from({length:12}, (_, i) => byYear[y]?.[i] ?? null).map((v,i) => (
                <td key={i} style={{textAlign:"right",padding:"4px 8px",borderBottom:"1px solid #f5f5f5",color:v==null?"var(--text-faint)":"inherit"}}>
                  {v==null ? "—" : v.toLocaleString("fr-FR",{maximumFractionDigits:0})}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Aperçu des valeurs (`monthlyByYear`/`indexByYear`) telles que renvoyées par `/engie/validate` —
 * mêmes données que celles envoyées à `/engie/ingest`, aucun appel serveur supplémentaire. */
function EngieValuesPreviewModal({ meters, isIncluded, onClose }: {
  meters: EngieMeterCandidate[]; isIncluded: (m: EngieMeterCandidate) => boolean; onClose: () => void;
}) {
  return (
    <Modal title="👁 Valeurs détectées dans le fichier" onClose={onClose} wide fixedHeight>
      <div style={{display:"flex",flexDirection:"column",flex:1,minHeight:0}}>
        <div style={{overflowY:"auto",padding:"20px 24px",flex:1}}>
          {meters.map(m => (
            <div key={m.key} style={{marginBottom:24,opacity:isIncluded(m)?1:0.45}}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
                <span>{m.energyType==="elec"?"⚡":"🔥"}</span>
                <strong style={{fontSize:13}}>{m.label}</strong>
                <span style={{fontSize:11,color:"var(--text-faint)"}}>({m.unit || "kWh"})</span>
                {!isIncluded(m) && <span style={{fontSize:11,color:"var(--text-faint)",fontStyle:"italic"}}>— non importé (case décochée)</span>}
              </div>
              <MonthlyValuesTable byYear={m.monthlyByYear}/>
              {m.indexByYear && Object.keys(m.indexByYear).length > 0 && (
                <div style={{marginTop:10}}>
                  <div style={{fontSize:11,color:"var(--text-muted)",marginBottom:4}}>Relevés d'index (compteur)</div>
                  <MonthlyValuesTable byYear={m.indexByYear}/>
                </div>
              )}
            </div>
          ))}
        </div>
        <div style={{padding:"16px 24px",borderTop:"1px solid #f0f0f0",background:"#fff",flexShrink:0,display:"flex",justifyContent:"flex-end"}}>
          <button onClick={onClose} className="btn btn-black">Fermer</button>
        </div>
      </div>
    </Modal>
  );
}
